/* promotion-model.js — the mode-adapter interface the Promotion wizard renders against.
 *
 * The wizard (promotion-ladder.js / promotion-wizard.js, later tasks) never branches on
 * getAuthMode() itself -- it calls the interface createPromotionModel() returns, and this
 * module is the ONLY place that knows hosted (`/api/connections/{cid}/promotion/*`, release-
 * based) and local (`/api/promotion/*`, tenant-name-based) are different backends. The two
 * plan() shapes in particular are normalized here (see plan() below) so any downstream
 * plan-rendering code reads one shape regardless of mode.
 *
 * `route = {source:{id,label,env,family}, target:{id,label,env,family}}` -- hosted ids are
 * connection ids (from `GET /api/connections`), local ids are tenant names (from
 * `GET /api/promotion/pipelines`). Building `route` from user picks is the wizard's job
 * (later tasks); this module only consumes route.source.id / route.target.id.
 *
 * `createPromotionModel({api, mode} = {})` accepts an optional injected {api, mode} pair so
 * promotion-model.test.mjs can exercise the hosted/local branches with fixtures instead of a
 * real fetch/DOM -- defaults to the real api.js singleton and the real getAuthMode().
 */

import { api as realApi } from './api.js';
import { getAuthMode } from './auth.js';

// dev < test < prod -- the only three rungs a family's ladder ever has.
const ENV_ORDER = ['dev', 'test', 'prod'];

// Byte-identical to promotion/gates.py:check_ladder's own failure reason -- exported so
// promotion-wizard.js imports this ONE JS declaration (rather than re-declaring the string a
// third time) both for ladderGate()'s own return below and for classifying a thrown hosted
// apply error's message back to the "ladder" gate chip. Two declarations remain (Python +
// this one), down from three.
export const LADDER_GATE_REASON = "This release must apply cleanly to TEST before it can go to PROD.";

// ── Pure helpers (unit-tested directly; no api/DOM dependency) ──────────────

/**
 * Group a flat connection-like list into per-family rungs + an unassigned tray.
 *
 * @param {Array<{id, label, family?, env_role?}>} connections
 * @returns {{families: Array<{family: string, rungs: {dev: Array, test: Array, prod: Array}, unassigned: Array}>}}
 *
 * A connection with no `family` cannot belong to any promotion pipeline and is dropped
 * entirely (it can never legally be one end of a route) -- this is also why a fully
 * unlabeled fleet renders as `families: []` (the wizard's first-run onboarding state).
 * Within a family, a connection whose `env_role` isn't one of dev/test/prod (missing,
 * blank, or unrecognized) lands in that family's `unassigned` tray instead of a rung.
 * `env_role` is matched case-insensitively; families are returned sorted alphabetically
 * for a stable render order (mirrors the backend's `derive_pipelines`).
 */
export function buildRungs(connections) {
  const byFamily = new Map();
  for (const c of connections || []) {
    const family = c && c.family ? String(c.family) : '';
    if (!family) continue; // no family -> not part of any pipeline
    if (!byFamily.has(family)) {
      byFamily.set(family, { family, rungs: { dev: [], test: [], prod: [] }, unassigned: [] });
    }
    const entry = byFamily.get(family);
    const env = c && c.env_role ? String(c.env_role).toLowerCase() : '';
    if (ENV_ORDER.includes(env)) entry.rungs[env].push(c);
    else entry.unassigned.push(c);
  }
  const families = [...byFamily.keys()].sort().map((f) => byFamily.get(f));
  return { families };
}

/**
 * The legal route segments for ONE family's rungs -- strictly ascending (dev->test,
 * dev->prod, test->prod) and only where BOTH ends have at least one candidate connection.
 * A rung with zero connections can't be either end of a segment, so e.g. a family with
 * only dev+test populated yields just [{from:"dev",to:"test"}] (no ...->prod pairs).
 *
 * @param {{dev: Array, test: Array, prod: Array}} rungs one family's rungs (buildRungs() output)
 * @returns {Array<{from: string, to: string}>}
 */
export function legalSegments(rungs) {
  const segments = [];
  for (let i = 0; i < ENV_ORDER.length; i++) {
    for (let j = i + 1; j < ENV_ORDER.length; j++) {
      const from = ENV_ORDER[i];
      const to = ENV_ORDER[j];
      if ((rungs && rungs[from] && rungs[from].length) && (rungs && rungs[to] && rungs[to].length)) {
        segments.push({ from, to });
      }
    }
  }
  return segments;
}

/**
 * Verbatim JS mirror of `promotion/gates.py:check_ladder` -- PROD may only receive a
 * release that already applied cleanly to TEST. Non-prod targets always pass; "partial"
 * and "empty" prior-run statuses do NOT satisfy the gate (only exactly "ok" does);
 * `releaseId == null` is a wildcard (any clean TEST run passes, not just this release's).
 * The failure reason string is surfaced verbatim in the UI -- keep it byte-identical to
 * the Python side.
 *
 * @param {string} targetEnv
 * @param {Array<{target_env: string, status: string, release_id?: string}>} priorRuns
 * @param {string|null} [releaseId]
 * @returns {{ok: boolean, reason: string}}
 */
export function ladderGate(targetEnv, priorRuns, releaseId) {
  if (targetEnv !== "prod") return { ok: true, reason: "ok" };
  for (const r of priorRuns || []) {
    if (r.target_env === "test" && r.status === "ok" &&
        (releaseId == null || r.release_id === releaseId)) return { ok: true, reason: "ok" };
  }
  return { ok: false, reason: LADDER_GATE_REASON };
}

// ── Local-mode ladder plumbing ───────────────────────────────────────────────

// Flatten `getPipelines()`'s {pipelines:[{family, stages:[{tenant,env,rank}]}]} into the
// same connection-like shape buildRungs() consumes, so both modes share one grouping impl.
// Every stage already carries a resolved family+env (derive_pipelines filters out anything
// unlabeled server-side), so the local ladder never populates an `unassigned` tray.
function _pipelinesToConnections(pipelines) {
  const out = [];
  for (const p of pipelines || []) {
    for (const s of (p && p.stages) || []) {
      out.push({ id: s.tenant, label: s.tenant, family: p.family, env_role: s.env });
    }
  }
  return out;
}

// ── The mode adapter ─────────────────────────────────────────────────────────

/**
 * @param {{api?: object, mode?: "hosted"|"local"}} [overrides] injection seam for tests --
 *   real callers pass nothing and get the live api.js singleton + the real auth mode.
 */
export function createPromotionModel(overrides = {}) {
  const api = overrides.api || realApi;
  // Mode is resolved once at construction (matches auth.js: a hosted<->local flip only
  // ever happens via a full page reload on login/logout, never mid-session).
  const mode = overrides.mode || (getAuthMode() === "hosted" ? "hosted" : "local");
  const hosted = mode === "hosted";

  return {
    mode,
    supportsReleases: hosted,
    supportsLiveProgress: !hosted,

    async loadLadder() {
      if (hosted) {
        const data = await api.connections();
        return buildRungs((data && data.connections) || []);
      }
      const data = await api.getPipelines();
      return buildRungs(_pipelinesToConnections(data && data.pipelines));
    },

    // Hosted only -- release history for the given source connection. Local has no release
    // concept; api.promotionReleases() itself already resolves {releases:[]} off-hosted, so
    // this is a straight delegation either way.
    releases(sourceCid) {
      return api.promotionReleases(sourceCid);
    },

    // Hosted only -- snapshot the source connection's current config into a portable
    // Release. api.promotionSnapshot() itself resolves {} off-hosted.
    snapshot(sourceCid) {
      return api.promotionSnapshot(sourceCid);
    },

    // Dry-run plan, normalized to one shape across both backends:
    //   hosted:  cid = route.target.id (the TARGET connection); {plan:[...], dry_run:true}
    //            -> {items: plan, allowed: true}                     (gates run separately)
    //   local:   source/target = route.source.id/route.target.id (tenant names);
    //            {plans:[...], allowed, reason} -> {items: plans, allowed, reason?}
    async plan(route, releaseId) {
      if (hosted) {
        const res = await api.promotionPlanHosted(route.target.id, releaseId);
        return { items: (res && res.plan) || [], allowed: true };
      }
      const res = await api.promotionPlan(route.source.id, route.target.id);
      const out = { items: (res && res.plans) || [], allowed: res ? res.allowed : true };
      if (res && res.reason !== undefined) out.reason = res.reason;
      return out;
    },

    // Gated real apply. `confirm` carries the typed PROD confirmation text (only meaningful
    // when the target is prod; both backends ignore it otherwise).
    apply(route, releaseId, { confirm } = {}) {
      if (hosted) return api.promotionApplyHosted(route.target.id, releaseId, { confirm });
      return api.promotionApply(route.source.id, route.target.id, { confirm, release_id: releaseId });
    },

    // Run history. api.promotionRuns(cid) already branches hosted/local + defaults cid to
    // the active connection, so this is a straight delegation.
    runs(cid) {
      return api.promotionRuns(cid);
    },
  };
}
